import { services as s } from "@/services";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
	QuickstartDataSchemaT,
	QuickstartInjectionDetailT,
	QuickstartShockwaveDetailT,
	QuickstartTrackerT,
	TemplateSettingsSchemaT,
	TrackingType,
} from "../../../types/zod/quickstart-settings";

// Actions
interface NewDiaryActions {
	getNewDiaryValues: () => TemplateSettingsSchemaT;
	// Admin Form Actions
	setOrganisation: (organisation: {
		organisation_id: string;
		organisation_name: string;
	}) => void;
	setContactId: (id: string) => void;
	setName: (name: string) => void;
	setDescription: (description: string) => void;

	// Trackers Form Actions
	setTrackers: (trackers: QuickstartTrackerT[]) => void;
	addTracker: (tracker: QuickstartTrackerT) => void;
	removeTracker: (index: number) => void;
	updateTracker: (index: number, tracker: Partial<QuickstartTrackerT>) => void;

	// Events Form Actions
	toggleEvent: (event: TrackingType) => void;
	setEvents: (events: TrackingType[]) => void;

	// Injection Form Actions
	setInjectionId: (id: string) => void;
	setReferringClinicianId: (id: string | null) => void;
	setInjectingClinicianId: (id: string | null) => void;
	setInjectionMethodId: (id: string | null) => void;
	setInjectionDetails: (details: {
		injection_details: QuickstartInjectionDetailT[];
	}) => void;

	// Surgery Form Actions
	setSurgeryId: (id: string) => void;
	setSurgeryReferringClinicianId: (id: string | null) => void;
	setSurgeryOperatingClinicianId: (id: string | null) => void;
	setSurgerySide: (side: string | null) => void;
	setSurgeryOrthopaedicArea: (area: string | null) => void;
	setSurgeryOptionId: (id: string | null) => void;

	// Education Form Actions
	setEducationId: (id: string) => void;
	setEducationName: (name: string) => void;
	setEducationDescription: (description: string | null) => void;
	setEducatedBy: (id: string | null) => void;

	// Shockwave Form Actions
	setShockwaveId: (id: string) => void;
	setShockwaveReferringClinicianId: (id: string | null) => void;
	setShockwavingClinicianId: (id: string | null) => void;
	setShockwaveDetails: (details: {
		shockwave_details: QuickstartShockwaveDetailT[];
	}) => void;

	// Generic setter
	setState: (state: Partial<QuickstartDataSchemaT>) => void;

	// Reset
	reset: () => void;
}

const initialState: TemplateSettingsSchemaT = {
	// Quickstart
	quickstart_key: "PAIN",

	// Admin Form
	organisation_id: "",
	organisation_name: "",
	contact_id: "",
	name: "",
	description: "",
	invited_by: "",
	invited_by_formal_name: "",

	// Trackers Form
	trackers: [{ form_id: "", schedule_id: "" }],

	// Events Form
	required_events: [],

	// Injection Form
	injection_id: s.uuidv4(),
	referring_clinician_id: "",
	injecting_clinician_id: "",
	injection_method_id: "",
	injection_details: [],

	// Surgery Form
	surgery_id: s.uuidv4(),
	operating_clinician_id: null,
	side: null,
	orthopaedic_area: null,
	surgery_option_id: null,

	// Education Form
	education_id: s.uuidv4(),
	education_name: "",
	education_description: null,
	educated_by: null,

	// Shockwave Form
	shockwave_id: s.uuidv4(),
	shockwaving_clinician_id: "",
	shockwave_details: [],
};

export const useTemplateStore = create<
	TemplateSettingsSchemaT & NewDiaryActions
>()(
	devtools(
		(set, get) => ({
			...initialState,

			getNewDiaryValues: () => {
				const state = get();
				return {
					...state,
					// Ensure all required fields are present and properly typed
					organisation_id: state.organisation_id,
					organisation_name: state.organisation_name,
					contact_id: state.contact_id,
					name: state.name,
					description: state.description,
					trackers: state.trackers,
					required_events: state.required_events,
					injection_id: state.injection_id,
					referring_clinician_id: state.referring_clinician_id,
					injecting_clinician_id: state.injecting_clinician_id,
					injection_method_id: state.injection_method_id,
					injection_details: state.injection_details,
					surgery_id: state.surgery_id,
					operating_clinician_id: state.operating_clinician_id,
					side: state.side,
					orthopaedic_area: state.orthopaedic_area,
					surgery_option_id: state.surgery_option_id,
					education_id: state.education_id,
					education_name: state.education_name,
					education_description: state.education_description,
					educated_by: state.educated_by,
					shockwave_id: state.shockwave_id,
					shockwaving_clinician_id: state.shockwaving_clinician_id,
					shockwave_details: state.shockwave_details,
				} as TemplateSettingsSchemaT;
			},
			// Admin Form Actions
			setOrganisation: (organisation) =>
				set({
					organisation_id: organisation.organisation_id,
					organisation_name: organisation.organisation_name,
				}),
			setContactId: (id) => set({ contact_id: id }),
			setName: (name) => set({ name }),
			setDescription: (description) => set({ description }),

			// Trackers Form Actions
			setTrackers: (trackers) => set({ trackers }),
			addTracker: (tracker) =>
				set((state) => ({
					trackers: [...state.trackers, tracker],
				})),
			removeTracker: (index) =>
				set((state) => ({
					trackers: state.trackers.filter((_, i) => i !== index),
				})),
			updateTracker: (index, tracker) =>
				set((state) => ({
					trackers: state.trackers.map((t, i) =>
						i === index ? { ...t, ...tracker } : t,
					),
				})),

			// Events Form Actions
			toggleEvent: (event) =>
				set((state) => {
					const newEvents = state.required_events.includes(event)
						? state.required_events.filter((e) => e !== event)
						: [...state.required_events, event];

					return {
						required_events: newEvents,
					};
				}),
			setEvents: (events) =>
				set(() => ({
					required_events: events,
				})),

			// Injection Form Actions
			setInjectionId: (id) => set({ injection_id: id }),
			setReferringClinicianId: (id) =>
				set({ referring_clinician_id: id ?? undefined }),
			setInjectingClinicianId: (id) =>
				set({ injecting_clinician_id: id ?? undefined }),
			setInjectionMethodId: (id) =>
				set({ injection_method_id: id ?? undefined }),
			setInjectionDetails: (details) =>
				set({ injection_details: details.injection_details }),

			// Surgery Form Actions
			setSurgeryId: (id) => set({ surgery_id: id }),
			setSurgeryReferringClinicianId: (id) =>
				set({ referring_clinician_id: id }),
			setSurgeryOperatingClinicianId: (id) =>
				set({ operating_clinician_id: id }),
			setSurgerySide: (side) => set({ side: side }),
			setSurgeryOrthopaedicArea: (area) => set({ orthopaedic_area: area }),
			setSurgeryOptionId: (id) => set({ surgery_option_id: id }),

			// Education Form Actions
			setEducationId: (id) => set({ education_id: id }),
			setEducationName: (name) => set({ education_name: name }),
			setEducationDescription: (description) =>
				set({ education_description: description }),
			setEducatedBy: (id) => set({ educated_by: id }),

			// Shockwave Form Actions
			setShockwaveId: (id) => set({ shockwave_id: id }),
			setShockwaveReferringClinicianId: (id) =>
				set({ referring_clinician_id: id ?? undefined }),
			setShockwavingClinicianId: (id) =>
				set({ shockwaving_clinician_id: id ?? undefined }),
			setShockwaveDetails: (details) =>
				set({ shockwave_details: details.shockwave_details }),

			// Generic setter
			setState: (state) =>
				set((currentState) => ({ ...currentState, ...state })),

			// Reset
			reset: () => set(initialState),
		}),
		{ name: "template-store" },
	),
);
